import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  InputLabel,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Search,
  MoreVert,
  Visibility,
  Send,
  Payment,
  Cancel,
  Delete,
  Receipt,
  CalendarMonth,
  Refresh,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useInvoices, useInvoiceStats, useSendInvoice, useResendInvoice, useDeleteInvoice } from '../../hooks/useInvoices';
import RecordPaymentModal from '../../components/invoices/RecordPaymentModal';
import ExtendDueDateModal from '../../components/invoices/ExtendDueDateModal';
import CancelInvoiceModal from '../../components/invoices/CancelInvoiceModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { COLORS } from '@elevair/shared';
import { Invoice } from '../../services/invoices';

type StatusFilter = 'all' | 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [extendDueDateModalOpen, setExtendDueDateModalOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Queries
  const { data, isLoading, error, refetch, isFetching } = useInvoices({
    page: page + 1,
    limit: rowsPerPage,
    status: status === 'all' ? undefined : status,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const { data: stats } = useInvoiceStats();
  const sendInvoice = useSendInvoice();
  const resendInvoice = useResendInvoice();
  const deleteInvoice = useDeleteInvoice();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, invoice: Invoice) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleView = () => {
    if (selectedInvoice) {
      navigate(`/invoices/${selectedInvoice._id}`);
    }
    handleMenuClose();
  };

  const handleSendConfirm = async () => {
    if (selectedInvoice) {
      await sendInvoice.mutateAsync(selectedInvoice._id);
    }
    setSendDialogOpen(false);
    setSelectedInvoice(null);
  };

  const handleResendConfirm = async () => {
    if (selectedInvoice) {
      await resendInvoice.mutateAsync({ id: selectedInvoice._id });
    }
    setResendDialogOpen(false);
    setSelectedInvoice(null);
  };

  const handleDeleteConfirm = async () => {
    if (selectedInvoice) {
      await deleteInvoice.mutateAsync(selectedInvoice._id);
    }
    setDeleteDialogOpen(false);
    setSelectedInvoice(null);
  };

  const statusConfig: Record<string, { color: string; label: string }> = {
    draft: { color: '#6B7280', label: 'Draft' },
    sent: { color: '#3B82F6', label: 'Sent' },
    paid: { color: '#10B981', label: 'Paid' },
    overdue: { color: '#EF4444', label: 'Overdue' },
    cancelled: { color: '#9CA3AF', label: 'Cancelled' },
    refunded: { color: '#F59E0B', label: 'Refunded' },
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  // Determine if invoice is overdue
  const getDisplayStatus = (invoice: Invoice) => {
    if (invoice.status === 'sent' && new Date(invoice.dueDate) < new Date() && invoice.amountDue > 0) {
      return 'overdue';
    }
    return invoice.status;
  };

  // Helper to determine row background color based on status and timing
  const getRowBackgroundColor = (invoice: Invoice): string => {
    const now = new Date();
    const dueDate = new Date(invoice.dueDate);
    const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    // Paid - Light Green
    if (invoice.status === 'paid') {
      return '#ECFDF5';
    }

    // Cancelled - Light Gray
    if (invoice.status === 'cancelled') {
      return '#F9FAFB';
    }

    // Draft - Light Blue
    if (invoice.status === 'draft') {
      return '#EFF6FF';
    }

    // For sent invoices, check timing
    if (invoice.status === 'sent') {
      // Overdue - Light Red
      if (daysUntilDue < 0) {
        return '#FEF2F2';
      }

      // Due within 7 days - Yellow to Orange gradient based on urgency
      if (daysUntilDue <= 7) {
        // More urgent (0-3 days) = more orange, less urgent (4-7 days) = more yellow
        if (daysUntilDue <= 3) {
          return '#FFF7ED'; // Light orange
        }
        return '#FEFCE8'; // Light yellow
      }
    }

    // Default - White
    return '#FFFFFF';
  };

  // Helper to get status indicator dot color
  const getStatusDotColor = (invoice: Invoice): string => {
    const now = new Date();
    const dueDate = new Date(invoice.dueDate);
    const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (invoice.status === 'paid') return '#10B981'; // Green
    if (invoice.status === 'cancelled') return '#9CA3AF'; // Gray
    if (invoice.status === 'draft') return '#3B82F6'; // Blue
    if (invoice.status === 'sent') {
      if (daysUntilDue < 0) return '#EF4444'; // Red - overdue
      if (daysUntilDue <= 3) return '#F97316'; // Orange - very soon
      if (daysUntilDue <= 7) return '#EAB308'; // Yellow - due soon
    }
    return '#6B7280'; // Default gray
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Invoices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatCurrency(stats?.totalOutstanding || 0)} outstanding
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/invoices/new')}
        >
          New Invoice
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        {[
          { label: 'Draft', value: stats?.draft || 0, status: 'draft' as const },
          { label: 'Sent', value: stats?.sent || 0, status: 'sent' as const },
          { label: 'Overdue', value: stats?.overdue || 0, status: 'overdue' as const },
          { label: 'Paid', value: stats?.paid || 0, status: 'paid' as const },
        ].map((stat) => (
          <Card
            key={stat.label}
            sx={{
              px: 3,
              py: 2,
              minWidth: 100,
              cursor: 'pointer',
              border: status === stat.status ? `2px solid ${statusConfig[stat.status]?.color || '#000'}` : '2px solid transparent',
            }}
            onClick={() => setStatus(stat.status)}
          >
            <Typography variant="h5" fontWeight={600}>
              {stat.value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {stat.label}
            </Typography>
          </Card>
        ))}
      </Box>

      {/* Revenue Card */}
      <Card sx={{ mb: 3, p: 2, bgcolor: '#F0FDF4' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Revenue (Paid Invoices)
            </Typography>
            <Typography variant="h4" fontWeight={600} color="success.main">
              {formatCurrency(stats?.totalRevenue || 0)}
            </Typography>
          </Box>
          <Receipt sx={{ fontSize: 48, color: '#86EFAC' }} />
        </Box>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            placeholder="Search invoices..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh invoices">
            <IconButton
              onClick={() => refetch()}
              disabled={isFetching}
              sx={{
                ml: 'auto',
                transition: 'transform 0.3s ease',
                ...(isFetching && {
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }),
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Card>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load invoices. Please try again.
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Issue Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No invoices found.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate('/invoices/new')}
                        sx={{ mt: 2 }}
                      >
                        Create Invoice
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.invoices.map((invoice) => {
                    const displayStatus = getDisplayStatus(invoice);
                    const rowBgColor = getRowBackgroundColor(invoice);
                    return (
                      <TableRow
                        key={invoice._id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: rowBgColor,
                          '&:hover': {
                            backgroundColor: rowBgColor,
                            filter: 'brightness(0.97)',
                          },
                        }}
                        onClick={() => navigate(`/invoices/${invoice._id}`)}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: getStatusDotColor(invoice),
                                flexShrink: 0,
                              }}
                            />
                            <Typography fontWeight={500}>
                              {invoice.invoiceNumber}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: invoice.clientId?.avatarColor || COLORS.primary,
                                fontSize: '0.875rem',
                              }}
                            >
                              {invoice.clientName?.split(' ').map((n: string) => n[0]).join('')}
                            </Avatar>
                            <Typography>{invoice.clientName}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {format(new Date(invoice.issueDate), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>
                          <Typography
                            color={displayStatus === 'overdue' ? 'error' : 'inherit'}
                            fontWeight={displayStatus === 'overdue' ? 600 : 400}
                          >
                            {format(new Date(invoice.dueDate), 'MMM d, yyyy')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box>
                            <Typography
                              fontWeight={500}
                              sx={{
                                textDecoration: invoice.status === 'cancelled' ? 'line-through' : 'none',
                                color: invoice.status === 'cancelled' ? 'text.disabled' : 'inherit',
                              }}
                            >
                              {formatCurrency(invoice.total)}
                            </Typography>
                            {invoice.status !== 'cancelled' && invoice.amountDue > 0 && invoice.amountDue !== invoice.total && (
                              <Typography variant="caption" color="text.secondary">
                                Due: {formatCurrency(invoice.amountDue)}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusConfig[displayStatus]?.label || displayStatus}
                            size="small"
                            sx={{
                              bgcolor: `${statusConfig[displayStatus]?.color}15`,
                              color: statusConfig[displayStatus]?.color,
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, invoice)}
                          >
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={data?.pagination.total || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Card>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleView}>
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          View Invoice
        </MenuItem>

        {selectedInvoice?.status === 'draft' && (
          <MenuItem onClick={() => { handleMenuClose(); setSendDialogOpen(true); }}>
            <Send fontSize="small" sx={{ mr: 1 }} />
            Send Invoice
          </MenuItem>
        )}

        {(selectedInvoice?.status === 'sent' || (selectedInvoice && getDisplayStatus(selectedInvoice) === 'overdue')) && (
          <MenuItem onClick={() => { handleMenuClose(); setResendDialogOpen(true); }}>
            <Send fontSize="small" sx={{ mr: 1 }} />
            Resend Invoice
          </MenuItem>
        )}

        {(selectedInvoice?.status === 'sent' || (selectedInvoice && getDisplayStatus(selectedInvoice) === 'overdue')) && (
          <MenuItem onClick={() => { handleMenuClose(); setExtendDueDateModalOpen(true); }}>
            <CalendarMonth fontSize="small" sx={{ mr: 1 }} />
            Extend Due Date
          </MenuItem>
        )}

        {(selectedInvoice?.status === 'sent' || (selectedInvoice && getDisplayStatus(selectedInvoice) === 'overdue')) && (
          <MenuItem onClick={() => { handleMenuClose(); setPaymentModalOpen(true); }}>
            <Payment fontSize="small" sx={{ mr: 1 }} />
            Record Payment
          </MenuItem>
        )}

        {selectedInvoice?.status !== 'paid' && selectedInvoice?.status !== 'cancelled' && (
          <MenuItem onClick={() => { handleMenuClose(); setCancelDialogOpen(true); }}>
            <Cancel fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
            <Typography color="error">Cancel Invoice</Typography>
          </MenuItem>
        )}

        {selectedInvoice?.status === 'draft' && (
          <MenuItem onClick={() => { handleMenuClose(); setDeleteDialogOpen(true); }}>
            <Delete fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
            <Typography color="error">Delete</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Record Payment Modal */}
      <RecordPaymentModal
        open={paymentModalOpen}
        invoice={selectedInvoice}
        onClose={() => {
          setPaymentModalOpen(false);
          setSelectedInvoice(null);
        }}
      />

      {/* Extend Due Date Modal */}
      <ExtendDueDateModal
        open={extendDueDateModalOpen}
        invoice={selectedInvoice}
        onClose={() => {
          setExtendDueDateModalOpen(false);
          setSelectedInvoice(null);
        }}
      />

      {/* Send Confirmation Dialog */}
      <ConfirmDialog
        open={sendDialogOpen}
        title="Send Invoice"
        message={`Send invoice ${selectedInvoice?.invoiceNumber} to ${selectedInvoice?.clientEmail}?`}
        confirmText="Send Invoice"
        variant="info"
        onConfirm={handleSendConfirm}
        onCancel={() => {
          setSendDialogOpen(false);
          setSelectedInvoice(null);
        }}
        loading={sendInvoice.isPending}
      />

      {/* Resend Confirmation Dialog */}
      <ConfirmDialog
        open={resendDialogOpen}
        title="Resend Invoice"
        message={`Resend invoice ${selectedInvoice?.invoiceNumber} to ${selectedInvoice?.clientEmail}?`}
        confirmText="Resend Invoice"
        variant="info"
        onConfirm={handleResendConfirm}
        onCancel={() => {
          setResendDialogOpen(false);
          setSelectedInvoice(null);
        }}
        loading={resendInvoice.isPending}
      />

      {/* Cancel Invoice Modal */}
      <CancelInvoiceModal
        open={cancelDialogOpen}
        invoice={selectedInvoice}
        onClose={() => {
          setCancelDialogOpen(false);
          setSelectedInvoice(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Invoice"
        message={`Are you sure you want to delete invoice ${selectedInvoice?.invoiceNumber}? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSelectedInvoice(null);
        }}
        loading={deleteInvoice.isPending}
      />
    </Box>
  );
}
